import React, { useState, useEffect } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import ClickableLink from './ClickableLink';

const Experiments = () => {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { trackPaperPdfOpen, trackPaperCodeClick } = useAnalytics();

  useEffect(() => {
    // Fetch the papers data from the JSON file
    fetch('/data/papers.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch papers data');
        }
        return response.json();
      })
      .then(data => {
        setPapers(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading papers:', error);
        setLoading(false);
      });
  }, []);

  // Function to highlight author's name
  const formatAuthors = (authors) => {
    return authors.split(', ').map((author, index, arr) => {
      const isLastAuthor = index === arr.length - 1;
      const separator = isLastAuthor ? '' : ', ';
      
      if (author.includes('E. Yu')) {
        return (
          <React.Fragment key={index}>
            <span><strong>{author}</strong>{separator}</span>
          </React.Fragment>
        );
      }
      return <React.Fragment key={index}>{author}{separator}</React.Fragment>;
    });
  };

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-3xl font-bold text-primary mb-8 text-center">Experiments</h1>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-primary">Loading papers...</p>
        </div>
      ) : papers.length === 0 ? (
        <div className="text-center text-primary">
          <p>No papers found.</p>
        </div>
      ) : (
        <div className="flex flex-col space-y-8 max-w-4xl mx-auto">
          {papers.map((paper, index) => (
            <div 
              key={index} 
              className="bg-card rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex flex-col md:flex-row">
                <div className="w-full md:w-1/3 relative">
                  <div 
                    className="aspect-[4/3] relative"
                    style={{ backgroundColor: paper.backgroundColor || '#374151' }}
                  >
                    <img 
                      src={`/images/papers/${paper.thumbnail}`} 
                      alt={paper.title}
                      className="absolute h-full w-full object-contain p-1"
                    />
                  </div>
                </div>
                <div className="p-4 w-full md:w-2/3">
                  <h2 className="text-xl font-semibold text-primary mb-2">{paper.title}</h2>
                  <p className="text-secondary mb-3">{formatAuthors(paper.authors)}</p>
                  {paper.abstract && (
                    <p className="text-primary mb-4 text-sm">{paper.abstract}</p>
                  )}
                  <div className="flex space-x-3">
                    {paper.pdfUrl && (
                      <ClickableLink
                        href={`/api/download/paper?slug=${paper.id}`}
                        onClick={() => trackPaperPdfOpen(paper.id, paper.title)}
                        preserveReferrer
                        className="px-3 py-1 border border-secondary text-secondary rounded-md transition-colors hover:bg-secondary hover:text-white"
                      >
                        PDF
                      </ClickableLink>
                    )}
                    {paper.codeUrl && (
                      <ClickableLink
                        href={paper.codeUrl}
                        onClick={() => trackPaperCodeClick(paper.id, paper.title, paper.codeUrl)}
                        className="px-3 py-1 border border-secondary text-secondary rounded-md transition-colors hover:bg-secondary hover:text-white"
                      >
                        Code
                      </ClickableLink>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Experiments;
